import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, Clock } from "lucide-react";
import { sendToWeb3Forms } from "@/lib/web3forms";
import { isValidContact, looksLikeEmail, PROPERTY_TYPES } from "@/lib/formHelpers";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  firma: "",
  kontakt: "",
  property_type: "",
  velikost: "",
};

const CalcForm = () => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.property_type) {
      setErr("Vyberte prosím typ prostoru.");
      return;
    }
    if (!isValidContact(form.kontakt)) {
      setErr("Zadejte prosím telefon nebo e-mail.");
      return;
    }
    setState("submitting");
    trackEvent("cta_click_ziskat_nabidku", { location: "kalkulace_form" });
    try {
      const isEmail = looksLikeEmail(form.kontakt);
      const safeFirma = form.firma?.trim() || "Web poptávka — Kalkulace";
      const [emailRes, dbRes] = await Promise.all([
        sendToWeb3Forms("Nová poptávka z webu — Kalkulace", {
          Firma: form.firma,
          Kontakt: form.kontakt,
          email: isEmail ? form.kontakt : "",
          telefon: isEmail ? "" : form.kontakt,
          "Typ prostoru": form.property_type,
          "Velikost prostoru": form.velikost,
          Zdroj: "Formulář Kalkulace",
        }),
        axios
          .post(`${API}/inquiries`, {
            name: safeFirma,
            phone: isEmail ? "" : form.kontakt,
            email: isEmail ? form.kontakt : "",
            property_type: form.property_type,
            size: form.velikost,
            message: "",
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error("[CalcForm] DB save failed:", e?.response?.data || e?.message || e);
            return null;
          }),
      ]);
      if (emailRes.success) {
        trackEvent("form_submit_kalkulace", {
          property_type: form.property_type,
          has_company: form.firma?.trim() ? "yes" : "no",
          db_saved: dbRes ? "yes" : "no",
        });
        setState("success");
        setForm(initial);
      } else {
        setState("error");
        setErr(
          "Odeslání se nezdařilo. Zkuste to prosím znovu nebo zavolejte na +420 775 601 254."
        );
      }
    } catch {
      setState("error");
      setErr(
        "Odeslání se nezdařilo. Zkuste to prosím znovu nebo zavolejte na +420 775 601 254."
      );
    }
  };

  const fieldCls =
    "w-full bg-white border border-[#E5E5E5] focus:border-[#0F5C69] focus:ring-2 focus:ring-[#0F5C69]/15 text-[#111111] placeholder:text-[#999999] px-4 py-3 text-sm focus:outline-none rounded-md transition-colors";
  const labelCls =
    "text-[11px] uppercase tracking-[0.2em] font-bold text-[#555555] mb-2 block";
  const hintCls = "mt-1.5 text-[12px] text-[#888888] leading-relaxed";
  const selectArrow = (color) => ({
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(
      color
    )}' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  });

  return (
    <section
      id="calc"
      data-testid="calc-section"
      className="relative bg-white py-20 md:py-28"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left side */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 02 ] — Kalkulace
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-[-0.025em] leading-[1.05] mt-5 text-[#111111]">
              Upřesněte poptávku{" "}
              <span className="font-light text-[#0F5C69]">
                (zabere ~30&nbsp;sekund)
              </span>
            </h2>
            <p className="mt-8 text-[#555555] leading-relaxed max-w-md text-base md:text-lg">
              Stačí pár údajů – zbytek doladíme s vámi po telefonu nebo e-mailu.
            </p>
            <div className="mt-10 flex items-center gap-4 border-l-2 border-[#0F5C69] pl-4">
              <Clock size={22} strokeWidth={1.5} className="text-[#0F5C69]" />
              <div className="text-sm text-[#555555]">
                <span className="font-semibold text-[#111111]">
                  Reakční doba do 24 h
                </span>{" "}
                — Brno a okolí
              </div>
            </div>

            <div
              data-testid="calc-after-submit"
              className="mt-12 pt-10 border-t border-[#E5E5E5]"
            >
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                Co se stane po odeslání
              </div>
              <ol className="mt-6 space-y-5">
                {[
                  { n: "01", t: "Ozveme se do 24 hodin" },
                  { n: "02", t: "Domluvíme nezávaznou prohlídku prostoru" },
                  {
                    n: "03",
                    t: "Připravíme cenovou nabídku na míru – bez skrytých poplatků",
                  },
                ].map((s) => (
                  <li
                    key={s.n}
                    data-testid={`calc-step-${s.n}`}
                    className="flex items-start gap-5"
                  >
                    <span className="font-display text-2xl md:text-3xl font-black tracking-[-0.04em] text-[#0F5C69] tabular-nums leading-none shrink-0 min-w-[2.5rem]">
                      {s.n}
                    </span>
                    <span className="text-[#222222] leading-relaxed text-base md:text-lg pt-1">
                      {s.t}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right side form */}
          <div className="lg:col-span-7">
            {state === "success" ? (
              <div
                data-testid="calc-success"
                className="bg-[#E6F2F4] border border-[#0F5C69]/20 p-8 md:p-10 rounded-2xl"
              >
                <CheckCircle2
                  size={40}
                  strokeWidth={1.5}
                  className="text-[#0F5C69]"
                />
                <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-5 text-[#111111]">
                  Máme to. Ozveme se do 24 hodin.
                </h3>
                <p className="mt-3 text-[#555555] text-sm leading-relaxed">
                  Pokud to spěchá, zavolejte nám na{" "}
                  <a
                    href="tel:+420775601254"
                    className="font-semibold text-[#0F5C69] underline"
                  >
                    +420 775 601 254
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={() => setState("idle")}
                  data-testid="calc-reset"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0F5C69] hover:gap-3 transition-all"
                >
                  Odeslat další kalkulaci{" "}
                  <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="calc-form"
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 md:p-10 shadow-[0_20px_50px_-20px_rgba(15,92,105,0.15)]"
              >
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Firma</label>
                    <input
                      data-testid="calc-firma"
                      type="text"
                      value={form.firma}
                      onChange={update("firma")}
                      placeholder="Např. ACME s.r.o."
                      className={fieldCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Kontakt *</label>
                    <input
                      data-testid="calc-kontakt"
                      type="text"
                      required
                      value={form.kontakt}
                      onChange={update("kontakt")}
                      placeholder="+420… nebo vy@firma.cz"
                      className={fieldCls}
                    />
                    <p className={hintCls}>
                      Preferujete e-mail? Napište e-mail. Zavoláme jen pokud
                      chcete.
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Typ prostoru *</label>
                    <select
                      data-testid="calc-property"
                      required
                      value={form.property_type}
                      onChange={update("property_type")}
                      className={`${fieldCls} appearance-none cursor-pointer pr-10`}
                      style={selectArrow("#0F5C69")}
                    >
                      <option value="" disabled>
                        Vyberte typ prostoru…
                      </option>
                      {PROPERTY_TYPES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Velikost prostoru</label>
                    <input
                      data-testid="calc-velikost"
                      type="text"
                      value={form.velikost}
                      onChange={update("velikost")}
                      placeholder="Např. malá kancelář / cca 200 m²"
                      className={fieldCls}
                    />
                    <p className={hintCls}>Stačí odhad – upřesníme spolu.</p>
                  </div>
                </div>

                {err && (
                  <div
                    data-testid="calc-error"
                    className="mt-5 text-sm text-[#8B1E1E] bg-[#FDECEC] border border-[#F3C6C6] px-4 py-3 rounded-md"
                  >
                    {err}
                  </div>
                )}

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    data-testid="calc-submit"
                    disabled={state === "submitting"}
                    className="group inline-flex items-center justify-center gap-3 bg-[#0F5C69] text-white h-14 px-8 font-semibold tracking-tight rounded-lg hover:bg-[#0B4B55] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {state === "submitting" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Odesílám…
                      </>
                    ) : (
                      <>
                        Získat nabídku
                        <ArrowRight
                          size={18}
                          strokeWidth={2.5}
                          className="transition-transform group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-[#888888] leading-relaxed max-w-xs">
                    Odesláním souhlasíte se zpracováním kontaktních údajů pro
                    potřeby vyřízení poptávky.
                  </p>
                </div>

                <ul
                  data-testid="calc-benefits"
                  className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] text-[#666666]"
                >
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
                    Ozveme se do 24 hodin
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
                    Bez závazků
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
                    Individuální nabídka
                  </li>
                </ul>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalcForm;
